import React from 'react';

/* ─────────────────────────────────────────────────────────────────
   "LEDGER" — concept: B2B financial analytics dashboard
   A fixed, self-contained visual identity (own palette + fonts) —
   does not follow the site's light/dark theme, by design.

   Pass `full` to render the complete, scrollable dashboard (used
   inside the concept modal); omit it for the small card preview.
───────────────────────────────────────────────────────────────── */

const SPARK_POINTS = [18, 22, 19, 27, 24, 32, 29, 38, 35, 44, 41, 52];

const TRANSACTIONS = [
  { name: 'Aarav Textiles Pvt Ltd', plan: 'Growth', amount: '₹18,400', status: 'Paid' },
  { name: 'Northwind Logistics', plan: 'Scale', amount: '₹42,900', status: 'Paid' },
  { name: 'Studio Verve', plan: 'Starter', amount: '₹3,200', status: 'Pending' },
  { name: 'Kavya Retail Group', plan: 'Growth', amount: '₹18,400', status: 'Paid' },
  { name: 'Orbit Freight Co.', plan: 'Scale', amount: '₹42,900', status: 'Failed' },
];

const TEAM = [
  { name: 'R. Menon', role: 'Finance Lead', init: 'RM' },
  { name: 'S. Iyer', role: 'Ops Manager', init: 'SI' },
  { name: 'A. Krishnan', role: 'Analyst', init: 'AK' },
];

const Sparkline = () => {
  const w = 240, h = 64, pad = 4;
  const max = Math.max(...SPARK_POINTS), min = Math.min(...SPARK_POINTS);
  const stepX = (w - pad * 2) / (SPARK_POINTS.length - 1);
  const toY = v => h - pad - ((v - min) / (max - min)) * (h - pad * 2);
  const points = SPARK_POINTS.map((v, i) => `${pad + i * stepX},${toY(v)}`).join(' ');
  const lastX = pad + (SPARK_POINTS.length - 1) * stepX;
  const lastY = toY(SPARK_POINTS[SPARK_POINTS.length - 1]);

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="ldg-spark" preserveAspectRatio="none">
      {[0.25, 0.5, 0.75].map(f => (
        <line key={f} x1={pad} x2={w - pad} y1={h * f} y2={h * f} className="ldg-spark-grid" />
      ))}
      <polyline points={points} className="ldg-spark-line" fill="none" />
      <circle cx={lastX} cy={lastY} r="3.5" className="ldg-spark-dot" />
    </svg>
  );
};

const STATUS_STYLE = {
  Paid: 'ldg-chip-good',
  Pending: 'ldg-chip-warn',
  Failed: 'ldg-chip-bad',
};

const LedgerDashboard = ({ full = false }) => (
  <div className={`ldg-frame ${full ? 'ldg-frame-full' : ''}`}>
    <div className="ldg-titlebar">
      <span className="ldg-dot" style={{ background: '#EA5F5F' }} />
      <span className="ldg-dot" style={{ background: '#E8B34C' }} />
      <span className="ldg-dot" style={{ background: '#4CAF7D' }} />
    </div>

    <div className="ldg-body">
      <aside className="ldg-rail">
        <span className="ldg-rail-mark">L</span>
        <span className="ldg-rail-item ldg-rail-active" />
        <span className="ldg-rail-item" />
        <span className="ldg-rail-item" />
        <span className="ldg-rail-item" />
      </aside>

      <div className="ldg-main">
        <div className="ldg-topbar">
          <div>
            <p className="ldg-eyebrow">OVERVIEW</p>
            <h3 className="ldg-title">Revenue</h3>
          </div>
          <span className="ldg-period">Last 30 days</span>
        </div>

        <div className="ldg-kpis">
          <div className="ldg-kpi">
            <p className="ldg-kpi-label">MRR</p>
            <p className="ldg-kpi-value">₹4,82,300</p>
            <span className="ldg-chip ldg-chip-good">+12.4%</span>
          </div>
          <div className="ldg-kpi">
            <p className="ldg-kpi-label">Active Accounts</p>
            <p className="ldg-kpi-value">1,204</p>
            <span className="ldg-chip ldg-chip-good">+3.1%</span>
          </div>
          <div className="ldg-kpi">
            <p className="ldg-kpi-label">Churn</p>
            <p className="ldg-kpi-value">1.8%</p>
            <span className="ldg-chip ldg-chip-bad">+0.3%</span>
          </div>
        </div>

        <div className="ldg-chart-panel">
          <div className="ldg-chart-head">
            <span>Net revenue trend</span>
            <span className="ldg-chart-total">₹52.4K</span>
          </div>
          <Sparkline />
        </div>

        {full && (
          <>
            <div className="ldg-table-panel">
              <div className="ldg-chart-head"><span>Recent transactions</span></div>
              <table className="ldg-table">
                <thead>
                  <tr><th>Account</th><th>Plan</th><th>Amount</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {TRANSACTIONS.map((t, i) => (
                    <tr key={i}>
                      <td>{t.name}</td>
                      <td className="ldg-muted-cell">{t.plan}</td>
                      <td className="ldg-mono-cell">{t.amount}</td>
                      <td><span className={`ldg-chip ${STATUS_STYLE[t.status]}`}>{t.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ldg-team-panel">
              <div className="ldg-chart-head"><span>Finance team</span></div>
              <div className="ldg-team-list">
                {TEAM.map(m => (
                  <div key={m.name} className="ldg-team-row">
                    <span className="ldg-avatar">{m.init}</span>
                    <div>
                      <p className="ldg-team-name">{m.name}</p>
                      <p className="ldg-team-role">{m.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>

    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=IBM+Plex+Mono:wght@500;600&display=swap');

      .ldg-frame {
        --bg: #0E1420; --panel: #161D2C; --border: #232C40;
        --text: #E7EAF2; --muted: #8891A6; --accent: #F2A93B;
        --good: #34C77B; --bad: #E8635B; --warn: #E8B34C;
        background: var(--bg); border: 1px solid var(--border); border-radius: 14px;
        overflow: hidden; font-family: 'Sora', sans-serif; box-shadow: 0 20px 50px rgba(0,0,0,0.35);
        width: 100%; height: 100%; display: flex; flex-direction: column;
      }
      .ldg-frame-full { height: auto; min-height: 100%; border-radius: 0; box-shadow: none; }
      .ldg-titlebar { display: flex; gap: 6px; padding: 12px 16px; background: var(--panel); border-bottom: 1px solid var(--border); }
      .ldg-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }

      .ldg-body { display: flex; flex: 1; min-height: 0; }
      .ldg-frame-full .ldg-body { min-height: auto; }
      .ldg-rail { width: 44px; background: var(--panel); border-right: 1px solid var(--border); display: flex; flex-direction: column; align-items: center; gap: 18px; padding: 16px 0; flex-shrink: 0; }
      .ldg-rail-mark { font-weight: 800; color: var(--accent); font-size: 15px; margin-bottom: 6px; }
      .ldg-rail-item { width: 18px; height: 18px; border-radius: 6px; background: rgba(255,255,255,0.06); }
      .ldg-rail-active { background: var(--accent); }

      .ldg-main { flex: 1; padding: 20px 22px; min-width: 0; }
      .ldg-frame-full .ldg-main { padding: 26px 28px 60px; }
      .ldg-topbar { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 18px; }
      .ldg-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 0.12em; color: var(--muted); margin: 0 0 4px; }
      .ldg-title { font-size: 19px; font-weight: 700; color: var(--text); margin: 0; text-wrap: balance; }
      .ldg-period { font-size: 11px; color: var(--muted); background: var(--panel); border: 1px solid var(--border); padding: 5px 10px; border-radius: 7px; }

      .ldg-kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 16px; }
      .ldg-kpi { background: var(--panel); border: 1px solid var(--border); border-radius: 10px; padding: 12px; }
      .ldg-kpi-label { font-size: 10px; color: var(--muted); margin: 0 0 6px; font-weight: 600; }
      .ldg-kpi-value { font-family: 'IBM Plex Mono', monospace; font-variant-numeric: tabular-nums; font-size: 16px; font-weight: 600; color: var(--text); margin: 0 0 6px; }
      .ldg-chip { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 999px; white-space: nowrap; }
      .ldg-chip-good { color: var(--good); background: rgba(52,199,123,0.12); }
      .ldg-chip-bad { color: var(--bad); background: rgba(232,99,91,0.12); }
      .ldg-chip-warn { color: var(--warn); background: rgba(232,179,76,0.14); }

      .ldg-chart-panel { background: var(--panel); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; margin-bottom: 16px; }
      .ldg-chart-head { display: flex; justify-content: space-between; font-size: 11px; color: var(--muted); margin-bottom: 6px; }
      .ldg-chart-total { font-family: 'IBM Plex Mono', monospace; color: var(--text); font-weight: 600; }
      .ldg-spark { width: 100%; height: 56px; display: block; }
      .ldg-spark-grid { stroke: var(--border); stroke-width: 1; }
      .ldg-spark-line { stroke: var(--accent); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
      .ldg-spark-dot { fill: var(--accent); }

      .ldg-table-panel, .ldg-team-panel { background: var(--panel); border: 1px solid var(--border); border-radius: 10px; padding: 16px; margin-bottom: 16px; }
      .ldg-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
      .ldg-table th { text-align: left; font-size: 10px; color: var(--muted); font-weight: 700; letter-spacing: 0.06em; padding: 0 10px 10px 0; border-bottom: 1px solid var(--border); }
      .ldg-table td { padding: 10px 10px 10px 0; border-bottom: 1px solid var(--border); color: var(--text); }
      .ldg-table tr:last-child td { border-bottom: none; }
      .ldg-muted-cell { color: var(--muted); }
      .ldg-mono-cell { font-family: 'IBM Plex Mono', monospace; font-variant-numeric: tabular-nums; }

      .ldg-team-list { display: flex; flex-direction: column; gap: 12px; }
      .ldg-team-row { display: flex; align-items: center; gap: 12px; }
      .ldg-avatar { width: 32px; height: 32px; border-radius: 50%; background: rgba(242,169,59,0.16); color: var(--accent); font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
      .ldg-team-name { font-size: 12.5px; font-weight: 600; color: var(--text); margin: 0; }
      .ldg-team-role { font-size: 11px; color: var(--muted); margin: 2px 0 0; }
    `}</style>
  </div>
);

export default LedgerDashboard;
